// Chart account settings: rename/re-code an editable account, or
// deactivate/reactivate it. Built-in (`is_system`) accounts are locked:
// the server rejects identity changes and deactivation, so the form
// disables those controls up front.

import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import { Link, useParams } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import {
  type ChartAccount,
  deactivateChartAccount,
  getChartAccount,
  reactivateChartAccount,
  updateChartAccount,
} from "@/lib/api";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function statusTone(active: boolean): StatusTone {
  return active ? "success" : "idle";
}

export function ChartAccountSettingsPage() {
  const { accountId } = useParams({ strict: false }) as { accountId: string };
  const [account, setAccount] = useState<ChartAccount | null>(null);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [subType, setSubType] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [confirming, setConfirming] = useState<
    "deactivate" | "reactivate" | null
  >(null);

  const load = useCallback(() => {
    if (!accountId) return;
    setError(null);
    getChartAccount(accountId)
      .then((loaded) => {
        setAccount(loaded);
        setCode(loaded.code);
        setName(loaded.name);
        setSubType(loaded.sub_type ?? "");
      })
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  }, [accountId]);

  useEffect(load, [load]);

  const isSystem = account?.is_system ?? false;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!account || isSystem) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const nextName = name.trim();
      if (!nextName) throw new Error("Account name is required.");
      const nextCode = code.trim();
      if (!nextCode) throw new Error("Account code is required.");
      const updated = await updateChartAccount(account.id, {
        code: nextCode,
        name: nextName,
        sub_type: subType.trim(),
      });
      setAccount(updated);
      setCode(updated.code);
      setName(updated.name);
      setSubType(updated.sub_type ?? "");
      setNotice("Account updated.");
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const changeActive = async () => {
    if (!account) return;
    const action = confirming;
    setConfirming(null);
    setActing(true);
    setError(null);
    setNotice(null);
    try {
      if (action === "deactivate") await deactivateChartAccount(account.id);
      else await reactivateChartAccount(account.id);
      load();
      setNotice(
        action === "deactivate"
          ? "Account deactivated. It stays in the chart and keeps its history."
          : "Account reactivated and selectable again.",
      );
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/chart-of-accounts/$accountId"
          params={{ accountId }}
          search={(previous) => previous}
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          {account?.code ?? accountId}
        </Link>
        <span
          aria-hidden="true"
          className="mx-2 text-[var(--muted-foreground)]"
        >
          /
        </span>
        <span aria-current="page" className="font-medium">
          Settings
        </span>
      </nav>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={load}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}

      {account === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : account ? (
        <>
          <PageHeader
            title="Account settings"
            description={
              <>
                <span className="font-mono">{account.code}</span> ·{" "}
                {account.name} ·{" "}
                <StatusDot
                  tone={statusTone(account.active)}
                  label={account.active ? "Active" : "Inactive"}
                />
                {account.is_system ? (
                  <>
                    {" "}
                    · <Badge tone="neutral">Built-in</Badge>
                  </>
                ) : null}
              </>
            }
          />

          {isSystem ? (
            <div
              role="note"
              className="surface rounded-xl border border-[var(--border)] p-4 text-sm"
            >
              Built-in accounts can&apos;t be re-coded, renamed, or deactivated.
              They back the platform&apos;s postings, so their code, name, and
              active status are fixed.
            </div>
          ) : null}

          <form
            onSubmit={submit}
            className="surface grid gap-4 rounded-2xl p-5"
            aria-label="Edit chart account"
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <label
                htmlFor="chart-account-code"
                className="grid gap-1 text-xs font-medium"
              >
                Code
                <Input
                  id="chart-account-code"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  required
                  disabled={isSystem || saving}
                />
              </label>
              <label
                htmlFor="chart-account-name"
                className="grid gap-1 text-xs font-medium"
              >
                Name
                <Input
                  id="chart-account-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                  disabled={isSystem || saving}
                />
              </label>
            </div>
            <label
              htmlFor="chart-account-sub-type"
              className="grid gap-1 text-xs font-medium"
            >
              Sub-type (optional)
              <Input
                id="chart-account-sub-type"
                value={subType}
                onChange={(event) => setSubType(event.target.value)}
                placeholder="e.g. cash, bank, accounts_receivable"
                disabled={isSystem || saving}
              />
            </label>
            <div>
              <Button
                type="submit"
                disabled={isSystem || saving}
                title={
                  isSystem
                    ? "Built-in accounts can't be re-coded or renamed"
                    : undefined
                }
              >
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </form>

          {!isSystem ? (
            <section
              aria-label="Account status"
              className="surface grid gap-3 rounded-2xl p-5"
            >
              <div>
                <h2 className="text-sm font-semibold">Status</h2>
                <p className="mt-1 text-sm text-[var(--muted-foreground)]">
                  Deactivating keeps the account and its history, but stops new
                  documents from selecting it.
                </p>
              </div>
              {account.active ? (
                <div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setConfirming("deactivate")}
                    disabled={acting}
                  >
                    {acting ? "Working…" : "Deactivate account"}
                  </Button>
                </div>
              ) : (
                <div>
                  <Button
                    type="button"
                    onClick={() => setConfirming("reactivate")}
                    disabled={acting}
                  >
                    {acting ? "Working…" : "Reactivate account"}
                  </Button>
                </div>
              )}
            </section>
          ) : null}
        </>
      ) : null}

      <Dialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        title={
          confirming === "deactivate"
            ? `Deactivate ${account?.name ?? "this account"}?`
            : `Reactivate ${account?.name ?? "this account"}?`
        }
        description={
          confirming === "deactivate"
            ? "The account stays in the chart and keeps its posted history, but new documents can no longer select it. You can reactivate it later."
            : "The account becomes selectable again for new documents and postings."
        }
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirming(null)}
            disabled={acting}
          >
            Cancel
          </Button>
          <Button type="button" onClick={() => void changeActive()} disabled={acting}>
            {confirming === "deactivate" ? "Deactivate account" : "Reactivate account"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
